import React, { FC, useMemo } from 'react';
import initStore from './initStore';
import {
    InputMode,
    CustomTextInput,
    DirectLineSession,
    useLocation,
    useConfig,
    DisableReason,
} from './hooks';
import { Components } from 'botframework-webchat';
import ReactChat from './ReactChat';
import { Activity, InitLoaderValues } from './types';
import { AmpFlagSet } from './amplitude';

import { LiveChatEndConfirmation } from './helpers';
import { MessageRenderer } from './interfaces/messageRenderer';
import { ApplicationModalPropsType } from './jsxElements';
import { useConversationStatus } from './contextProviders/ConversationStatusContext';
import { useInformationalAlert } from './contextProviders';

const { Composer } = Components;

const WebChat: FC<{
    directLine: DirectLineSession;
    featureFlags: AmpFlagSet;
    setCustomTextInput: (CustomTextInput: CustomTextInput) => void;
    setInputMode: (inputMode: InputMode) => void;
    setDisableTextSend: (value: boolean, reason?: DisableReason) => void;
    healthSystemToken?: string | null;
    initLoaderValues?: InitLoaderValues | null;
    modalProperties?: ApplicationModalPropsType;
    setModalProperties?: (props: ApplicationModalPropsType) => void | void;
    interactedWithBot: boolean;
    setFeedbackDialogIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setInteractedWithBot: (value: boolean) => void;
    setLiveChatEndConfirmation: React.Dispatch<
        React.SetStateAction<LiveChatEndConfirmation>
    >;
    skipGreeting: boolean;
    greetingMessages: Activity[];
    messageRenderer: MessageRenderer;
}> = ({
    directLine,
    featureFlags,
    setInputMode,
    setCustomTextInput,
    setDisableTextSend,
    healthSystemToken,
    initLoaderValues,
    modalProperties,
    setModalProperties,
    interactedWithBot,
    setFeedbackDialogIsOpen,
    setInteractedWithBot,
    setLiveChatEndConfirmation,
    skipGreeting,
    greetingMessages,
    messageRenderer,
}) => {
    const config = useConfig();
    const location = useLocation();

    const { setInactive } = useConversationStatus();
    const { showGlobalInformationAlert } = useInformationalAlert();

    const store = useMemo(
        () =>
            initStore(
                directLine.session,
                featureFlags,
                skipGreeting,
                setCustomTextInput,
                setInputMode,
                setInactive,
                showGlobalInformationAlert,
                setDisableTextSend,
                config,
                healthSystemToken,
                initLoaderValues,
                location
            ),
        [
            directLine.session,
            featureFlags,
            setCustomTextInput,
            setInputMode,
            setInactive,
            showGlobalInformationAlert,
            setDisableTextSend,
            healthSystemToken,
            initLoaderValues,
            location,
        ]
    );

    return (
        <Composer
            directLine={directLine.client}
            store={store}
            userID={directLine.session.userId}
            username={directLine.session.userName}
        >
            <ReactChat
                featureFlags={featureFlags}
                modalProperties={modalProperties}
                setModalProperties={setModalProperties}
                interactedWithBot={interactedWithBot}
                setFeedbackDialogIsOpen={setFeedbackDialogIsOpen}
                setInputMode={setInputMode}
                setInteractedWithBot={setInteractedWithBot}
                setLiveChatEndConfirmation={setLiveChatEndConfirmation}
                greetingMessages={greetingMessages}
                messageRenderer={messageRenderer}
            />
        </Composer>
    );
};

export default WebChat;
